import {EventEmitter, Signal, signal, WritableSignal} from "@angular/core"
import {Vector2Like} from "@cm/math"
import {deepCopy} from "@cm/utils"
import {Operator, OperatorPanelComponentType, OperatorProcessingHints} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator"
import {OperatorBase} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator-base"
import {OperatorCallback} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator-callback"
import {HalInvertMask} from "@app/textures/texture-editor/operator-stack/operators/layer-and-mask/hal/hal-invert-mask"
import {LayerAndMaskPanelComponent} from "@app/textures/texture-editor/operator-stack/operators/layer-and-mask/panel/layer-and-mask-panel.component"
import {LayerAndMaskToolbox} from "@app/textures/texture-editor/operator-stack/operators/layer-and-mask/toolbox/layer-and-mask-toolbox"
import * as TextureEditNodes from "@app/textures/texture-editor/texture-edit-nodes"
import {
    AnyImageOpResult,
    AnyImageOpResultNode,
    ValueOrNode,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {prepareAndBakeMinimalMask} from "@app/textures/texture-editor/operator-stack/operators/shared/nodes/mask-brush-bake/prepare-and-bake-minimal-mask"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {ImageOpNodeOperatorLayerAndMask} from "@app/textures/texture-editor/operator-stack/operators/layer-and-mask/node/node"
import {BrushSettings} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/brush-strokes/definition"
import type {BlendMode} from "@app/textures/texture-editor/operator-stack/operators/layer-and-mask/operator-layer-and-mask-types"
import {LayerEditMode, LayerMoveMode} from "@app/textures/texture-editor/operator-stack/operators/layer-and-mask/operator-layer-and-mask-types"
import {BakedMaskCache} from "@app/textures/texture-editor/operator-stack/operators/shared/nodes/mask-brush-bake/baked-mask-cache"

export class OperatorLayerAndMask extends OperatorBase<TextureEditNodes.OperatorLayerAndMask> {
    readonly showGuidesChanged = new EventEmitter<boolean>()
    readonly layerEditModeChanged = new EventEmitter<LayerEditMode>()
    readonly layerMoveModeChanged = new EventEmitter<LayerMoveMode>()

    readonly panelComponentType: OperatorPanelComponentType = LayerAndMaskPanelComponent
    readonly canvasToolbox: LayerAndMaskToolbox

    readonly type = "operator-layer-and-mask" as const

    constructor(callback: OperatorCallback, node: TextureEditNodes.OperatorLayerAndMask | null) {
        super(
            callback,
            deepCopy(node) ?? {
                type: "operator-layer-and-mask",
                enabled: true,
                maskReference: {
                    type: "data-object-reference",
                    dataObjectId: "",
                },
                mapOffsetInPixels: {x: 0, y: 0},
                maskOffsetInPixels: {x: 0, y: 0},
                layerMinOpacity: 0,
                layerMaxOpacity: 1,
                maskFeathering: 0,
                blendMode: "alpha",
            },
        )

        this.canvasToolbox = new LayerAndMaskToolbox(this)
        this._$brushSettings = signal<BrushSettings>(this.canvasToolbox.brushToolboxItem.brushSettings, {
            equal: () => false,
        })
        this.$brushSettings = this._$brushSettings.asReadonly()
        this.canvasToolbox.userInterationFinished.subscribe(() => {
            // since we're only blending in simple mode during interaction we need to request evaluation after being done with the interaction
            if (this.$blendMode() === "laplacian-pyramid") {
                this.requestEval()
            }
        })
        // reset cached mask pyramids when mask changes
        // this.canvasToolbox.brushToolboxItem.brushStrokeUpdated.subscribe(() => this.cachedMaskGaussianPyramid.dispose())
        // reset whole cache when not selected to save memory
        // this.callback.selectedOperatorChanged.pipe(takeUntil(this.unsubscribe)).subscribe((selectedOperator) => {
        //     if (selectedOperator !== this) {
        //         this.disposeCachedPyramids()
        //     }
        // })

        this.halInvertMask = new HalInvertMask(this.callback.halContext)
    }

    // OperatorBase
    // override async init() {
    //     await super.init()
    //     this._maskDrawableImageHandle = await this.callback.imageOpContextWebGL2.createDrawableImageHandle(
    //         this.node.maskReference.dataObjectId
    //             ? {
    //                   dataObjectId: this.node.maskReference.dataObjectId,
    //                   channelLayout: "R",
    //                   dataType: "uint8norm",
    //               }
    //             : {
    //                   width: 1,
    //                   height: 1,
    //                   channelLayout: "R",
    //                   dataType: "uint8norm",
    //               },
    //     )
    //     // this.canvasToolbox.brushToolboxItem.setDrawableImage(this._maskDrawableImageHandle.ref, this.node.maskReference.dataObjectId ? "fill" : "reset")
    // }

    // OperatorBase
    override dispose(): void {
        this.sharedImageOpNodeBag.dispose()
        this.maskBakeCache.dispose()
        super.dispose()
        // this.disposeCachedPyramids()
        // this._maskDrawableImageHandle.release()
        this.canvasToolbox.remove()
        this.halInvertMask.dispose()
    }

    // OperatorBase
    async clone(): Promise<Operator> {
        const clonedOperator = new OperatorLayerAndMask(this.callback, deepCopy(this.node))
        await clonedOperator.init()
        clonedOperator.canvasToolbox.brushToolboxItem.copyFrom(this.canvasToolbox.brushToolboxItem)
        return clonedOperator
    }

    private sharedImageOpNodeBag = new ImageOpNodeBag("OperatorLayerAndMaskShared")
    private maskBakeCache = new BakedMaskCache(this.callback.texturesApi, this.callback.organization.legacyId)

    // OperatorBase
    override async createImageOpGraph(sourceImage: AnyImageOpResultNode, hints: OperatorProcessingHints, imageOpNodeBag: ImageOpNodeBag) {
        const maskImage: ValueOrNode<AnyImageOpResult> = await this.sharedImageOpNodeBag.runInFrame(() =>
            prepareAndBakeMinimalMask({
                sharedImageOpNodeBag: this.sharedImageOpNodeBag,
                sourceImage,
                mode: hints.mode,
                dataObjectId: this.node.maskReference.dataObjectId ? this.node.maskReference.dataObjectId : undefined,
                maskRegion: this.node.maskRegion,
                brushToolboxItem: this.canvasToolbox.brushToolboxItem,
                maskBakeCache: this.maskBakeCache,
                onBaked: (dataObjectId, region) => {
                    this.node.maskReference.dataObjectId = dataObjectId
                    this.node.maskRegion = region
                },
            }),
        )
        return imageOpNodeBag.getNode("layerAndMask", ImageOpNodeOperatorLayerAndMask, {
            sourceImage,
            mapOffsetInPixels: this.mapOffsetInPixels,
            maskImage: maskImage,
            maskOffsetInPixels: this.maskOffsetInPixels,
            maskFeathering: this.node.maskFeathering ?? 0,
            drawMaskOnly: hints.mode === "preview" && this.$showMask() && this.selected,
            layerMinOpacity: this.node.layerMinOpacity ?? 0,
            layerMaxOpacity: this.node.layerMaxOpacity ?? 1,
            useLaplacianPyramid: (this.node.blendMode ?? "alpha") === "laplacian-pyramid" && !this.canvasToolbox.isUserInteracting,
        })
    }

    get mapOffsetInPixels(): Vector2Like {
        return this.node.mapOffsetInPixels
    }

    set mapOffsetInPixels(value: Vector2Like) {
        if (this.node.mapOffsetInPixels.x === value.x && this.node.mapOffsetInPixels.y === value.y) {
            return
        }
        this.node.mapOffsetInPixels = {x: value.x, y: value.y}
        // this.cachedForegroundLaplacianPyramid.dispose()
        this.markEdited()
        this.requestEval()
    }

    get maskOffsetInPixels(): Vector2Like {
        return this.node.maskOffsetInPixels
    }

    set maskOffsetInPixels(value: Vector2Like) {
        if (this.node.maskOffsetInPixels.x === value.x && this.node.maskOffsetInPixels.y === value.y) {
            return
        }
        this.node.maskOffsetInPixels = {x: value.x, y: value.y}
        // this.cachedMaskGaussianPyramid.dispose()
        this.markEdited()
        this.requestEval()
    }

    private readonly _$layerMinOpacity = signal(this.node.layerMinOpacity ?? 0)
    readonly $layerMinOpacity = this._$layerMinOpacity.asReadonly()
    setLayerMinOpacity(value: number): void {
        if (this.node.layerMinOpacity === value) {
            return
        }
        this.node.layerMinOpacity = value
        this._$layerMinOpacity.set(value)
        // this.cachedMaskGaussianPyramid.dispose()
        this.markEdited()
        this.requestEval()
    }

    private readonly _$layerMaxOpacity = signal(this.node.layerMaxOpacity ?? 1)
    readonly $layerMaxOpacity = this._$layerMaxOpacity.asReadonly()
    setLayerMaxOpacity(value: number): void {
        if (this.node.layerMaxOpacity === value) {
            return
        }
        this.node.layerMaxOpacity = value
        this._$layerMaxOpacity.set(value)
        // this.cachedMaskGaussianPyramid.dispose()
        this.markEdited()
        this.requestEval()
    }

    private readonly _$maskFeathering = signal(this.node.maskFeathering ?? 0)
    readonly $maskFeathering = this._$maskFeathering.asReadonly()
    setMaskFeathering(value: number): void {
        if (this.node.maskFeathering === value) {
            return
        }
        this.node.maskFeathering = value
        this._$maskFeathering.set(value)
        // this.cachedMaskGaussianPyramid.dispose()
        this.markEdited()
        this.requestEval()
    }

    private readonly _$showGuides = signal(true)
    readonly $showGuides = this._$showGuides.asReadonly()
    setShowGuides(value: boolean): void {
        if (this.$showGuides() === value) {
            return
        }
        this._$showGuides.set(value)
        this.showGuidesChanged.emit(value)
    }

    private readonly _$showMask = signal(false)
    readonly $showMask = this._$showMask.asReadonly()
    setShowMask(value: boolean): void {
        if (this.$showMask() === value) {
            return
        }
        this._$showMask.set(value)
        this.requestEval()
    }

    private readonly _$brushSettings!: WritableSignal<BrushSettings>
    readonly $brushSettings!: Signal<BrushSettings>
    setBrushSettings(value: BrushSettings): void {
        this._$brushSettings.set(value)
        this.canvasToolbox.brushToolboxItem.brushSettings = value
    }

    private readonly _$blendMode = signal<BlendMode>(this.node.blendMode ?? "alpha")
    readonly $blendMode = this._$blendMode.asReadonly()
    setBlendMode(value: BlendMode): void {
        if (this.node.blendMode === value) {
            return
        }
        this.node.blendMode = value
        this._$blendMode.set(value)
        // if (value !== "laplacian-pyramid") {
        //     this.disposeCachedPyramids()
        // }
        this.markEdited()
        this.requestEval()
    }

    private readonly _$layerEditMode = signal(LayerEditMode.Move)
    readonly $layerEditMode = this._$layerEditMode.asReadonly()
    setLayerEditMode(value: LayerEditMode): void {
        if (this.$layerEditMode() === value) {
            return
        }
        this._$layerEditMode.set(value)
        this.layerEditModeChanged.emit(value)
    }

    private readonly _$layerMoveMode = signal(LayerMoveMode.ImageAndMask)
    readonly $layerMoveMode = this._$layerMoveMode.asReadonly()
    setLayerMoveMode(value: LayerMoveMode): void {
        if (this._layerMoveSettings.layerMoveMode === value) {
            return
        }
        this._layerMoveSettings.layerMoveMode = value
        this._$layerMoveMode.set(value)
        this.layerMoveModeChanged.emit(value)
    }

    // async invertMask(): Promise<void> {
    //     const context = this.callback.imageOpContextWebGL2
    //     const cmdQueue = context.createCommandQueue()
    //
    //     cmdQueue.beginScope("invert-mask")
    //     const painter = cmdQueue.createPainter(
    //         "compositor",
    //         "invert-mask",
    //         `
    //         vec4 computeColor(ivec2 targetPixel) {
    //             return 1.0 - texelFetch0(targetPixel);
    //         }
    //         `,
    //     )
    //     const maskImage = drawableImage(cmdQueue, {
    //         drawableImageHandle: this._maskDrawableImageHandle.ref,
    //     })
    //     const maskImageCopy = copyRegion(cmdQueue, {
    //         sourceImage: maskImage,
    //     })
    //     cmdQueue.paint(painter, {
    //         sourceImages: maskImageCopy,
    //         resultImage: maskImage,
    //     })
    //     cmdQueue.endScope("invert-mask")
    //
    //     const [evaluatedMaskImage] = await cmdQueue.execute([maskImage])
    //     evaluatedMaskImage.release()
    //
    //     this.callback.drawableImageCache.markDirty(this._maskDrawableImageHandle.ref.id)
    //     this.markEdited()
    //     this.requestEval()
    // }

    // override async save(processingJobId: string) {
    //     // this.node.maskReference.dataObjectId = await this.callback.drawableImageCache.getDataObjectId(this._maskDrawableImageHandle.ref.id)
    //     if (!this.node.maskReference.dataObjectId) {
    //         throw new Error("Data-object id for mask not set")
    //     }
    //     return super.save(processingJobId)
    // }

    // private disposeCachedPyramids() {
    //     this.cachedBackgroundLaplacianPyramid.dispose()
    //     this.cachedForegroundLaplacianPyramid.dispose()
    //     this.cachedMaskGaussianPyramid.dispose()
    // }

    private _layerMoveSettings = new LayerMoveSettings()
    // private _brushSettings: BrushSettings = {
    //     color: 1,
    //     width: 150,
    //     hardness: 0.8,
    // }
    // private _maskDrawableImageHandle!: ManagedDrawableImageHandle

    // private cachedBackgroundLaplacianPyramid = new CachedLaplacianImagePyramid()
    // private cachedForegroundLaplacianPyramid = new CachedLaplacianImagePyramid()
    // private cachedMaskGaussianPyramid = new CachedGaussianImagePyramid()

    private halInvertMask: HalInvertMask // TODO replace by image-op
}

export {LayerEditMode, LayerMoveMode} from "../../../operator-stack/operators/layer-and-mask/operator-layer-and-mask-types"
export type {BlendMode} from "../../../operator-stack/operators/layer-and-mask/operator-layer-and-mask-types"

export class LayerMoveSettings {
    readonly changed = new EventEmitter<void>()

    set layerMoveMode(value: LayerMoveMode) {
        this._layerMoveMode = value
        this.changed.emit()
    }

    get layerMoveMode(): LayerMoveMode {
        return this._layerMoveMode
    }

    private _layerMoveMode = LayerMoveMode.ImageAndMask
}
